<template>
  <CSidebar
    aside
    overlaid
    size="lg"
    colorScheme="light"
    :show="$store.state.optionGroupsAssignmentAsideShow"
    @update:show="(val) => $store.commit('set', ['optionGroupsAssignmentAsideShow', val])"
  >
    <div class="sidebar-wrapper">
      <div class="sidebar-content">
        <div class="d-flex justify-content-between align-items-center mb-3">
          <h5 class="mb-0">Assign Option Groups</h5>
          <CSidebarClose @click.native="$store.commit('toggle', 'optionGroupsAssignmentAsideShow')" />
        </div>

        <div v-if="loading" class="text-center p-5">
          <CSpinner color="primary" />
          <div class="mt-3">Loading option groups...</div>
        </div>

        <template v-else>
          <!-- Selected Items Summary -->
          <div class="selected-items-summary mb-4">
            <h6 class="font-weight-bold">Selected Menu Items ({{ selectedItems.length }})</h6>
            <ul class="pl-3 mb-0">
              <li v-for="(item, index) in selectedItems" :key="'item-'+index" class="mb-1">
                {{ item.menu_name }}
              </li>
            </ul>
          </div>

          <!-- Reset Option Checkbox -->
          <div class="reset-option mb-3 p-2 rounded shadow-sm bg-warning-light">
            <div class="d-flex align-items-center">
              <CInputCheckbox :checked.sync="resetAllOptions" custom id="reset-options-checkbox"/>
              <label for="reset-options-checkbox" class="font-weight-bold mb-0 ml-2">
                Reset all option groups and reassign selected ones
              </label>
            </div>
            <small class="text-muted d-block pl-4 mt-1">
              This will remove all existing option groups from selected menu items before assigning new ones.
              When checked, you can also reorder selected option groups.
            </small>
          </div>

          <!-- Selected Option Groups (When Reset is Enabled) -->
          <div v-if="resetAllOptions" class="mb-4">
            <h6 class="font-weight-bold mb-0">
              <CIcon name="cil-list-low-priority" class="align-bottom text-success" />
              Selected Option Groups
            </h6>
            <small class="d-block text-muted mb-3">(Drag to reorder)</small>

            <div v-if="selectedOptionGroups.length > 0" class="selected-groups-container" ref="selectedGroupsList">
              <div v-for="(group, idx) in selectedOptionGroups" :key="'selected-group-' + group.id"
                class="option-group rounded shadow-sm mb-3 selected-group"
                :data-group-id="group.id">
                <div class="d-flex align-items-center">
                  <CIcon name="cil-move" class="mr-2 group-handle text-muted" />
                  <CInputCheckbox :checked.sync="group.selected" custom @change="handleUnselect(group)" />
                  <span class="font-weight-bold">{{ group.name }}</span>
                  <span class="ml-2 badge badge-info">{{ idx + 1 }}</span>

                  <CButton class="ml-auto" size="sm" variant="ghost" @click="toggleOptions(group.id)">
                    <CIcon :name="group.showOptions ? 'cil-chevron-top' : 'cil-chevron-bottom'" />
                  </CButton>
                </div>

                <!-- Options List (Collapsible) -->
                <div v-if="group.showOptions">
                  <OptionsList :group="group" />
                </div>
              </div>
            </div>
            <div v-else class="text-muted text-center p-3 border rounded">
              No selected option groups
            </div>
          </div>

          <!-- Available Option Groups -->
          <div>
            <h6 class="font-weight-bold mb-3">
              <CIcon name="cil-list" class="align-bottom text-primary" />
              {{ resetAllOptions ? 'Unselected' : 'Available' }} Option Groups
            </h6>

            <div v-if="displayOptionGroups.length > 0">
              <div v-for="(group, idx) in displayOptionGroups" :key="'group-' + group.id" class="option-group rounded shadow-sm mb-3">
                <div class="d-flex align-items-center">
                  <CInputCheckbox :checked.sync="group.selected" custom/>
                  <span class="font-weight-bold">{{ group.name }}</span>

                  <CButton class="ml-auto" size="sm" variant="ghost" @click="toggleOptions(group.id)">
                    <CIcon :name="group.showOptions ? 'cil-chevron-top' : 'cil-chevron-bottom'" />
                  </CButton>
                </div>

                <!-- Options List (Collapsible) -->
                <div v-if="group.showOptions">
                  <OptionsList :group="group" />
                </div>
              </div>
            </div>

            <div v-else class="text-muted text-center p-3 border rounded">
              No option groups available
            </div>
          </div>
        </template>
      </div>

      <!-- Action Buttons -->
      <div class="action-buttons" v-if="!loading">
        <CButton @click="$store.commit('toggle', 'optionGroupsAssignmentAsideShow')" color="link">
          Cancel
        </CButton>

        <CButton @click="assignOptionGroups" size="sm" color="primary" :disabled="!hasSelectedGroups || submitting">
          <CSpinner v-if="submitting" color="light" size="sm" />
          {{ resetAllOptions ? 'Reset and Assign Option Groups' : 'Assign Option Groups' }}
        </CButton>
      </div>
    </div>
  </CSidebar>
</template>

<script>
import Sortable from "sortablejs";
import OptionsList from "./components/OptionsList.vue";

export default {
  name: 'OptionGroupsAssignmentSidebar',
  components: {
    OptionsList
  },
  props: {
    restaurantId: { type: Number|String, required: true },
    selectedItems: { type: Array, required: true, default: () => [] }
  },
  data() {
    return {
      loading: false,
      submitting: false,
      availableOptionGroups: [],
      resetAllOptions: true,
      sortable: null,
    };
  },
  computed: {
    reloadParams() {
      return [this.$store.state.optionGroupsAssignmentAsideShow, this.selectedItems];
    },
    hasSelectedGroups() {
      return this.availableOptionGroups.some(group => group.selected);
    },
    selectedOptionGroups() {
      return this.availableOptionGroups
        .filter(group => group.selected)
        .sort((a, b) => a.sortOrder - b.sortOrder);
    },
    displayOptionGroups() {
      // When resetAllOptions is enabled, show unselected groups at the bottom
      if (this.resetAllOptions) {
        return this.availableOptionGroups.filter(group => !group.selected);
      }
      // Otherwise show all groups
      return this.availableOptionGroups;
    }
  },
  watch: {
    '$store.state.optionGroupsAssignmentAsideShow'(val) {
      if (val) {
        this.loadOptionGroups();
      }
    },
    resetAllOptions(val) {
      // When toggle reset option
      this.$nextTick(() => {
        if (val) {
          this.initSortable();
        } else {
          this.destroySortable();
        }
      });
    },
    selectedOptionGroups: {
      deep: true,
      handler() {
        this.$nextTick(() => {
          if (this.resetAllOptions && this.selectedOptionGroups.length > 0) {
            this.initSortable();
          }
        });
      }
    }
  },
  methods: {
    async loadOptionGroups() {
      this.loading = true;

      try {
        const url = new URL(this.$backend.MENU_V2.GET_OPTION_GROUPS(this.restaurantId));
        const response = await this.$axios.get(url);

        this.availableOptionGroups = response.data.map((group, index) => ({
          ...group,
          showOptions: false,
          selected: false,
          sortOrder: index // Default sort order based on API response
        }));

        // Reset the reset option when loading new data
        this.resetAllOptions = true;
      } catch (error) {
        console.error('Error loading option groups:', error);
        this.$toast.error('Failed to load option groups');
        this.availableOptionGroups = [];
      } finally {
        this.loading = false;
      }
    },

    toggleOptions(groupId) {
      const index = this.availableOptionGroups.findIndex(g => g.id === groupId);
      if (index !== -1) {
        this.$set(this.availableOptionGroups[index], 'showOptions', !this.availableOptionGroups[index].showOptions);
      }
    },

    handleUnselect(group) {
      if (!group.selected && this.resetAllOptions) {
        this.$nextTick(() => {
          this.updateSortOrder();
        });
      }
    },

    initSortable() {
      this.$nextTick(() => {
        // Destroy existing sortable if it exists
        this.destroySortable();

        // Initialize sortable for selected groups list
        if (this.$refs.selectedGroupsList) {
          this.sortable = Sortable.create(this.$refs.selectedGroupsList, {
            handle: '.group-handle',
            animation: 150,
            ghostClass: 'option-group-ghost',
            chosenClass: 'option-group-chosen',
            dragClass: 'option-group-drag',
            onEnd: this.updateSortOrder
          });
        }
      });
    },

    destroySortable() {
      if (this.sortable) {
        this.sortable.destroy();
        this.sortable = null;
      }
    },

    updateSortOrder() {
      // Get the actual DOM order after sorting
      if (this.$refs.selectedGroupsList) {
        const groupElements = this.$refs.selectedGroupsList.children;
        const sortedIds = Array.from(groupElements).map(el => parseInt(el.dataset.groupId));

        // Update sort order in the data model
        sortedIds.forEach((id, index) => {
          const realIndex = this.availableOptionGroups.findIndex(g => g.id === id);
          if (realIndex !== -1) {
            this.$set(this.availableOptionGroups[realIndex], 'sortOrder', index);
          }
        });

        // Force Vue to re-render the component to update indices
        this.$forceUpdate();
      }
    },

    async assignOptionGroups() {
      if (!this.hasSelectedGroups) return;

      this.submitting = true;

      try {
        // Use selectedOptionGroups to maintain sort order when reset mode is enabled
        const selectedGroups = this.resetAllOptions
          ? this.selectedOptionGroups
          : this.availableOptionGroups.filter(group => group.selected);

        const selectedGroupData = selectedGroups.map((group, index) => ({
          id: group.id,
          order: index
        }));

        const menuIds = this.selectedItems.map(item => item.id);

        const requestData = {
          option_groups: selectedGroupData,
          menu_ids: menuIds.join(','),
          reset_all: this.resetAllOptions // Send reset flag to backend
        };

        const url = new URL(this.$backend.MENU_V2.BULK.ASSIGN_OPTION_GROUPS(this.restaurantId));
        await this.$axios.post(url, requestData);

        this.$toast.success(`Option groups assigned to ${this.selectedItems.length} menu items successfully`);
        this.$store.commit('set', ['optionGroupsAssignmentAsideShow', false]);
      } catch (error) {
        this.$toast.error('Failed to assign option groups');
      } finally {
        this.submitting = false;
      }
    }
  }
}
</script>

<style scoped>
.sidebar-wrapper {
  display: flex;
  flex-direction: column;
  height: 100%;
  position: relative;
}

.sidebar-content {
  flex: 1;
  padding: 1rem 1.5rem 0 1rem;
  overflow-y: auto;
}

.selected-items-summary {
  max-height: 200px;
  overflow-y: auto;
  background: #f8f9fa;
  padding: 0.75rem;
  border-radius: 0.25rem;
  border: 1px solid #e9ecef;
}

.option-group {
  background: #fafafa;
  padding: 0.75rem 0.5rem;
  border: 1px solid #e9ecef;
  transition: all 0.2s ease;
}

.reset-option {
  background-color: #fff3cd;
  border: 1px solid #ffeeba;
}

.selected-group {
  background-color: #e8f4ff;
  border-color: #20a8d8;
}

.selected-group:hover {
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
}

.group-handle {
  cursor: move;
  color: #aaa;
}

.group-handle:hover {
  color: #666;
}

/* Ghost class while dragging */
.option-group-ghost {
  opacity: 0.6;
  background: #c8ebfb;
}

/* Chosen class when item is selected */
.option-group-chosen {
  background-color: #e8f4ff;
  border-color: #20a8d8;
}

/* Drag class for active dragging */
.option-group-drag {
  background-color: #e8f4ff;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
}

.action-buttons {
  position: sticky;
  bottom: 0;
  left: 0;
  right: 0;
  background: white;
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.1);
  padding: 1rem 1.5rem;
  display: flex;
  justify-content: space-between;
  z-index: 999;
  border-top: 1px solid #e9ecef;
}
</style>
